import {FC, useState} from 'react'
import type React from 'react'
import {api} from '../../services/api'
import {usePaginated} from '../../hooks/usePaginated'
import {Pagination} from '../../components/Pagination'

interface Booking {
  id: string
  bookingRef: string
  providerName: string
  providerCategory: string
  subtotal: number
  depositAmount: number
  remainingAmount: number
  status: string
  createdAt: string
  payments?: {id: string; amount: number; method: string; status: string; type: string}[]
  event?: {id: string; eventName?: string; eventDate?: string; venueAddress?: string}
  eventType?: {id: number; label: string}
  user?: {id: string; firstName: string; lastName: string; email: string}
}

function fmtDate(d?: string): string {
  if (!d) return '—'
  try { return new Date(d).toISOString().slice(0, 10) } catch { return d }
}

function extractState(addr?: string): string {
  if (!addr) return '—'
  const parts = addr.split(',')
  return parts[parts.length - 1]?.trim() || '—'
}

function statusStyle(s: string) {
  if (s === 'confirmed' || s === 'paid') return {bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0'}
  if (s === 'cancelled' || s === 'canceled') return {bg: '#fff1f2', color: '#e11d48', border: '#fecdd3'}
  return {bg: '#fefce8', color: '#ca8a04', border: '#fde68a'}
}

function displayStatus(s: string) {
  if (s === 'paid') return 'confirmed'
  if (s === 'canceled') return 'cancelled'
  return s
}

// ── View Modal ────────────────────────────────────────────────────────────────
const BookingModal: FC<{booking: Booking; onClose: () => void}> = ({booking, onClose}) => {
  const ss = statusStyle(booking.status)
  const label = displayStatus(booking.status)
  const ref = booking.bookingRef || `BK-${booking.id.slice(0, 6).toUpperCase()}`

  const Row: FC<{label: string; value: React.ReactNode}> = ({label: l, value}) => (
    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0'}}>
      <span style={{fontSize: 14, color: '#94a3b8'}}>{l}:</span>
      <span style={{fontSize: 14, fontWeight: 700, color: '#0f172a', textAlign: 'right'}}>{value}</span>
    </div>
  )

  const SectionHeader: FC<{title: string; iconBg: string; icon: React.ReactNode}> = ({title, iconBg, icon}) => (
    <div style={{display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12}}>
      <div style={{width: 48, height: 48, borderRadius: 12, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>
        {icon}
      </div>
      <span style={{fontSize: 20, fontWeight: 700, color: '#0f172a'}}>{title}</span>
    </div>
  )

  return (
    <div onClick={onClose} style={{position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20}}>
      <div onClick={e => e.stopPropagation()} style={{background: '#fff', borderRadius: 20, width: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.18)'}}>

        {/* Header */}
        <div style={{padding: '24px 28px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between'}}>
          <div>
            <div style={{fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 4}}>Booking Details</div>
            <div style={{fontSize: 14, color: '#94a3b8', fontWeight: 500}}>{ref}</div>
          </div>
          <button onClick={onClose} style={{background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4, marginTop: 2}}>
            <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><line x1='18' y1='6' x2='6' y2='18'/><line x1='6' y1='6' x2='18' y2='18'/></svg>
          </button>
        </div>

        {/* Body */}
        <div style={{padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 28}}>

          {/* Event Information */}
          <div>
            <SectionHeader
              title='Event Information'
              iconBg='#eff6ff'
              icon={<svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='#3b82f6' strokeWidth='2'><rect x='3' y='4' width='18' height='18' rx='2'/><line x1='16' y1='2' x2='16' y2='6'/><line x1='8' y1='2' x2='8' y2='6'/><line x1='3' y1='10' x2='21' y2='10'/></svg>}
            />
            <Row label='Event'      value={booking.event?.eventName || '—'} />
            <Row label='Category'   value={booking.eventType?.label || booking.providerCategory || '—'} />
            <Row label='Event Date' value={fmtDate(booking.event?.eventDate)} />
            <Row label='Tickets'    value={1} />
          </div>

          {/* Customer Information */}
          <div>
            <SectionHeader
              title='Customer Information'
              iconBg='#f0fdf4'
              icon={<svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='#22c55e' strokeWidth='2'><path d='M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2'/><circle cx='12' cy='7' r='4'/></svg>}
            />
            <Row label='Name'         value={booking.user ? `${booking.user.firstName} ${booking.user.lastName}` : '—'} />
            <Row label='Email'        value={booking.user?.email || '—'} />
            <Row label='State'        value={extractState(booking.event?.venueAddress)} />
            <Row label='Booking Date' value={fmtDate(booking.createdAt)} />
          </div>

          {/* Payment Information */}
          <div>
            <SectionHeader
              title='Payment Information'
              iconBg='#f5f3ff'
              icon={<svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='#8b5cf6' strokeWidth='2'><rect x='1' y='4' width='22' height='16' rx='2'/><line x1='1' y1='10' x2='23' y2='10'/></svg>}
            />
            {(() => {
              const pays = booking.payments ?? []
              const paidOf = (type: string) => pays.filter(p => p.type === type && p.status === 'completed').reduce((s, p) => s + Number(p.amount || 0), 0)
              const depositPaid = paidOf('deposit')
              const remainingPaid = paidOf('remaining')
              const pill = (paid: boolean) => (
                <span style={{padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: paid ? '#f0fdf4' : '#fefce8', color: paid ? '#16a34a' : '#a16207', border: `1px solid ${paid ? '#bbf7d0' : '#fde68a'}`}}>
                  {paid ? 'Paid' : 'Pending'}
                </span>
              )
              return (
                <>
                  <Row label='Total Amount' value={`$${Number(booking.subtotal).toFixed(2)}`} />
                  <Row label='Deposit (30%)' value={
                    <span style={{display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end'}}>
                      <span style={{fontWeight: 700}}>${Number(booking.depositAmount).toFixed(2)}</span>
                      {pill(depositPaid >= Number(booking.depositAmount) && Number(booking.depositAmount) > 0)}
                    </span>
                  } />
                  <Row label='Remaining Balance' value={
                    <span style={{display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end'}}>
                      <span style={{fontWeight: 700}}>${Number(booking.remainingAmount).toFixed(2)}</span>
                      {pill(remainingPaid >= Number(booking.remainingAmount) && Number(booking.remainingAmount) > 0)}
                    </span>
                  } />
                  <Row label='Paid to Date' value={`$${(depositPaid + remainingPaid).toFixed(2)}`} />
                </>
              )
            })()}
            <Row label='Payment Method' value='Credit Card' />
            <Row label='Status'         value={
              <span style={{padding: '3px 14px', borderRadius: 20, fontSize: 13, fontWeight: 500, background: ss.bg, color: ss.color, border: `1px solid ${ss.border}`}}>
                {label}
              </span>
            } />
          </div>

        </div>
      </div>
    </div>
  )
}

// ── Event Ticket types + modal ──────────────────────────────────────────────
interface Ticket {
  id: string
  ticketRef: string
  tier: string
  status: string
  quantity: number
  totalAmount: number
  faceValue: number
  paymentMethod?: string
  couponCode?: string
  createdAt: string
  checkedInCount?: number
  event?: {id: string; eventName?: string; eventDate?: string; category?: string; venueAddress?: string}
  user?: {id: string; firstName: string; lastName: string; email: string}
}

const TicketModal: FC<{ticket: Ticket; onClose: () => void}> = ({ticket, onClose}) => {
  const ss = statusStyle(ticket.status === 'active' ? 'confirmed' : ticket.status)
  const Row: FC<{label: string; value: React.ReactNode}> = ({label: l, value}) => (
    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0'}}>
      <span style={{fontSize: 14, color: '#94a3b8'}}>{l}:</span>
      <span style={{fontSize: 14, fontWeight: 700, color: '#0f172a', textAlign: 'right'}}>{value}</span>
    </div>
  )
  const SectionHeader: FC<{title: string; iconBg: string; icon: React.ReactNode}> = ({title, iconBg, icon}) => (
    <div style={{display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12}}>
      <div style={{width: 48, height: 48, borderRadius: 12, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>{icon}</div>
      <span style={{fontSize: 20, fontWeight: 700, color: '#0f172a'}}>{title}</span>
    </div>
  )
  const fee = Math.max(0, Number(ticket.totalAmount) - Number(ticket.faceValue))
  return (
    <div onClick={onClose} style={{position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20}}>
      <div onClick={e => e.stopPropagation()} style={{background: '#fff', borderRadius: 20, width: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.18)'}}>
        <div style={{padding: '24px 28px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between'}}>
          <div>
            <div style={{fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 4}}>Ticket Details</div>
            <div style={{fontSize: 14, color: '#94a3b8', fontWeight: 500}}>{ticket.ticketRef}</div>
          </div>
          <button onClick={onClose} style={{background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4, marginTop: 2}}>
            <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><line x1='18' y1='6' x2='6' y2='18'/><line x1='6' y1='6' x2='18' y2='18'/></svg>
          </button>
        </div>
        <div style={{padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 28}}>
          <div>
            <SectionHeader title='Event Information' iconBg='#eff6ff'
              icon={<svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='#3b82f6' strokeWidth='2'><rect x='3' y='4' width='18' height='18' rx='2'/><line x1='16' y1='2' x2='16' y2='6'/><line x1='8' y1='2' x2='8' y2='6'/><line x1='3' y1='10' x2='21' y2='10'/></svg>} />
            <Row label='Event' value={ticket.event?.eventName || '—'} />
            <Row label='Event Date' value={fmtDate(ticket.event?.eventDate)} />
            <Row label='Ticket Type' value={ticket.tier || 'General'} />
            <Row label='Quantity' value={ticket.quantity} />
            <Row label='Checked In' value={`${ticket.checkedInCount ?? 0} / ${ticket.quantity}`} />
          </div>
          <div>
            <SectionHeader title='Customer Information' iconBg='#f0fdf4'
              icon={<svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='#22c55e' strokeWidth='2'><path d='M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2'/><circle cx='12' cy='7' r='4'/></svg>} />
            <Row label='Name' value={ticket.user ? `${ticket.user.firstName} ${ticket.user.lastName}` : '—'} />
            <Row label='Email' value={ticket.user?.email || '—'} />
            <Row label='Purchase Date' value={fmtDate(ticket.createdAt)} />
          </div>
          <div>
            <SectionHeader title='Payment Information' iconBg='#f5f3ff'
              icon={<svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='#8b5cf6' strokeWidth='2'><rect x='1' y='4' width='22' height='16' rx='2'/><line x1='1' y1='10' x2='23' y2='10'/></svg>} />
            <Row label='Face Value' value={`$${Number(ticket.faceValue).toFixed(2)}`} />
            <Row label='Service Fee + Tax' value={`$${fee.toFixed(2)}`} />
            <Row label='Total Paid' value={`$${Number(ticket.totalAmount).toFixed(2)}`} />
            {ticket.couponCode ? <Row label='Coupon' value={ticket.couponCode} /> : null}
            <Row label='Payment Method' value={ticket.paymentMethod === 'free' ? 'Free' : 'Credit Card'} />
            <Row label='Status' value={
              <span style={{padding: '3px 14px', borderRadius: 20, fontSize: 13, fontWeight: 500, background: ss.bg, color: ss.color, border: `1px solid ${ss.border}`}}>
                {ticket.status}
              </span>
            } />
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
const BookingsPage: FC = () => {
  const [stateFilter, setState]   = useState('all')
  const [statusFilter, setStatus] = useState('all')
  const [catFilter, setCat]       = useState('all')
  const [viewing, setViewing]     = useState<Booking | null>(null)
  const [tab, setTab]             = useState<'service' | 'tickets'>('service')
  const [viewingTicket, setViewingTicket] = useState<Ticket | null>(null)

  // ── Service bookings: server-side pagination + search ──────────────────────
  const {
    items: filtered, total: bookingsTotal, page: bPage, setPage: setBPage,
    search, setSearch, limit: bLimit, loading, error, reload,
  } = usePaginated<Booking>(
    (p, l, s) => api.getBookingsPaged(p, l, s, {
      state:    stateFilter !== 'all' ? stateFilter : undefined,
      status:   statusFilter !== 'all' ? statusFilter : undefined,
      category: catFilter !== 'all' ? catFilter : undefined,
    }),
    {limit: 20, deps: [stateFilter, statusFilter, catFilter]},
  )

  // ── Ticket bookings: own server-side pagination + search ───────────────────
  const {
    items: filteredTickets, total: ticketsTotal, page: tPage, setPage: setTPage,
    search: tSearch, setSearch: setTSearch, limit: tLimit,
    loading: tLoading, error: tError, reload: tReload,
  } = usePaginated<Ticket>(
    (p, l, s) => api.getTicketsPaged(p, l, s),
    {limit: 20},
  )

  const states     = Array.from(new Set(filtered.map(b => extractState(b.event?.venueAddress)).filter(s => s !== '—'))).sort()
  const categories = Array.from(new Set(filtered.map(b => b.eventType?.label || b.providerCategory).filter(Boolean))).sort()

  const counts = {
    total:     bookingsTotal,
    confirmed: filtered.filter(b => ['confirmed', 'paid'].includes(b.status)).length,
    pending:   filtered.filter(b => b.status === 'pending').length,
    cancelled: filtered.filter(b => ['cancelled', 'canceled'].includes(b.status)).length,
  }

  return (
    <div>
      {/* Header */}
      <div style={{marginBottom: 16}}>
        <h1 style={{fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 4px'}}>Bookings</h1>
        <p style={{fontSize: 14, color: '#64748b', margin: 0}}>Manage service bookings and event ticket purchases</p>
      </div>

      {/* Tabs */}
      <div style={{display: 'flex', gap: 8, marginBottom: 16}}>
        {([
          {key: 'service', label: `Service Bookings (${bookingsTotal})`},
          {key: 'tickets', label: `Ticket Bookings (${ticketsTotal})`},
        ] as const).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              padding: '9px 18px', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer',
              border: `1px solid ${tab === t.key ? '#3b82f6' : '#e2e8f0'}`,
              background: tab === t.key ? '#3b82f6' : '#fff',
              color: tab === t.key ? '#fff' : '#475569',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'tickets' ? (
        <>
          {/* Search */}
          <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '16px 20px', marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.05)'}}>
            <div style={{position: 'relative'}}>
              <svg style={{position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8'}} width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                <circle cx='11' cy='11' r='8'/><line x1='21' y1='21' x2='16.65' y2='16.65'/>
              </svg>
              <input type='text' placeholder='Search by ticket #, event, or customer...' value={tSearch} onChange={e => setTSearch(e.target.value)}
                style={{width: '100%', padding: '10px 12px 10px 38px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#fff', outline: 'none', boxSizing: 'border-box'}} />
            </div>
          </div>

          {tError && (
            <div style={{padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 14, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <span>{tError}</span>
              <button onClick={tReload} style={{color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: 13}}>Retry</button>
            </div>
          )}

          <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden'}}>
            {tLoading ? (
              <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 0'}}>
                <div style={{width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite'}} />
              </div>
            ) : (
              <div style={{overflowX: 'auto'}}>
                <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
                  <thead>
                    <tr style={{borderBottom: '1px solid #e2e8f0'}}>
                      {['TICKET #', 'EVENT', 'CUSTOMER', 'TYPE', 'QTY', 'TOTAL PAID', 'STATUS', 'ACTIONS'].map(h => (
                        <th key={h} style={{padding: '12px 20px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTickets.map((t, i) => {
                      const ts = statusStyle(t.status === 'active' ? 'confirmed' : t.status)
                      return (
                        <tr key={t.id}
                          style={{borderBottom: i < filteredTickets.length - 1 ? '1px solid #f1f5f9' : 'none'}}
                          onMouseEnter={ev => (ev.currentTarget.style.background = '#fafafa')}
                          onMouseLeave={ev => (ev.currentTarget.style.background = 'transparent')}>
                          <td style={{padding: '16px 20px', color: '#64748b', fontWeight: 500, whiteSpace: 'nowrap', fontSize: 13}}>{t.ticketRef}</td>
                          <td style={{padding: '16px 20px', minWidth: 200}}>
                            <div style={{fontWeight: 700, color: '#0f172a'}}>{t.event?.eventName || '—'}</div>
                            <div style={{fontSize: 12, color: '#94a3b8', marginTop: 2}}>{fmtDate(t.event?.eventDate)}</div>
                          </td>
                          <td style={{padding: '16px 20px', minWidth: 180}}>
                            <div style={{fontWeight: 600, color: '#0f172a'}}>{t.user ? `${t.user.firstName} ${t.user.lastName}` : '—'}</div>
                            {t.user?.email && <div style={{fontSize: 12, color: '#94a3b8', marginTop: 2}}>{t.user.email}</div>}
                          </td>
                          <td style={{padding: '16px 20px', color: '#374151', whiteSpace: 'nowrap'}}>{t.tier || 'General'}</td>
                          <td style={{padding: '16px 20px', color: '#374151', whiteSpace: 'nowrap'}}>{t.quantity}</td>
                          <td style={{padding: '16px 20px', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap'}}>${Number(t.totalAmount).toFixed(2)}</td>
                          <td style={{padding: '16px 20px', whiteSpace: 'nowrap'}}>
                            <span style={{padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: ts.bg, color: ts.color, border: `1px solid ${ts.border}`}}>{t.status}</span>
                          </td>
                          <td style={{padding: '16px 20px', whiteSpace: 'nowrap'}}>
                            <button onClick={() => setViewingTicket(t)}
                              style={{display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', fontWeight: 600, fontSize: 14, padding: 0}}>
                              <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z'/><circle cx='12' cy='12' r='3'/></svg>
                              View
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                    {filteredTickets.length === 0 && (
                      <tr><td colSpan={8} style={{padding: '48px 20px', textAlign: 'center', color: '#94a3b8'}}>No ticket purchases found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{padding: '12px 20px', borderTop: '1px solid #f1f5f9', fontSize: 13, color: '#94a3b8'}}>
              Showing {filteredTickets.length} of {ticketsTotal} ticket purchases
            </div>
          </div>

          <Pagination page={tPage} limit={tLimit} total={ticketsTotal} onPageChange={setTPage} />

          {viewingTicket && <TicketModal ticket={viewingTicket} onClose={() => setViewingTicket(null)} />}
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </>
      ) : (
      <>
      {/* Filters */}
      <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '16px 20px', marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', display: 'flex', gap: 12, alignItems: 'center'}}>
        <div style={{position: 'relative', flex: 1}}>
          <svg style={{position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8'}} width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <circle cx='11' cy='11' r='8'/><line x1='21' y1='21' x2='16.65' y2='16.65'/>
          </svg>
          <input type='text' placeholder='Search bookings...' value={search} onChange={e => setSearch(e.target.value)}
            style={{width: '100%', padding: '10px 12px 10px 38px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#fff', outline: 'none', boxSizing: 'border-box'}} />
        </div>
        <select value={stateFilter} onChange={e => setState(e.target.value)}
          style={{flex: 1, padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#374151', background: '#fff', cursor: 'pointer', outline: 'none'}}>
          <option value='all'>All States</option>
          {states.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatus(e.target.value)}
          style={{flex: 1, padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#374151', background: '#fff', cursor: 'pointer', outline: 'none'}}>
          <option value='all'>All Status</option>
          <option value='confirmed'>Confirmed</option>
          <option value='pending'>Pending</option>
          <option value='cancelled'>Cancelled</option>
        </select>
        <select value={catFilter} onChange={e => setCat(e.target.value)}
          style={{flex: 1, padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#374151', background: '#fff', cursor: 'pointer', outline: 'none'}}>
          <option value='all'>All Categories</option>
          {categories.map(c => <option key={c} value={c!}>{c}</option>)}
        </select>
      </div>

      {/* Stat cards */}
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16}}>
        {[
          {label: 'Total Bookings', value: counts.total,     color: '#3b82f6'},
          {label: 'Confirmed',      value: counts.confirmed, color: '#22c55e'},
          {label: 'Pending',        value: counts.pending,   color: '#ca8a04'},
          {label: 'Cancelled',      value: counts.cancelled, color: '#ef4444'},
        ].map(s => (
          <div key={s.label} style={{background: '#fff', borderRadius: 12, padding: '20px 24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)'}}>
            <div style={{fontSize: 13, color: '#64748b', marginBottom: 8}}>{s.label}</div>
            <div style={{fontSize: 36, fontWeight: 700, color: s.color, lineHeight: 1}}>{s.value}</div>
          </div>
        ))}
      </div>

      {error && (
        <div style={{padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 14, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <span>{error}</span>
          <button onClick={reload} style={{color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: 13}}>Retry</button>
        </div>
      )}

      {/* Table */}
      <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden'}}>
        {loading ? (
          <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 0'}}>
            <div style={{width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite'}} />
          </div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
              <thead>
                <tr style={{borderBottom: '1px solid #e2e8f0'}}>
                  {['BOOKING #', 'EVENT', 'CUSTOMER', 'STATE', 'EVENT DATE', 'AMOUNT', 'STATUS', 'ACTIONS'].map(h => (
                    <th key={h} style={{padding: '12px 20px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((b, i) => {
                  const ss = statusStyle(b.status)
                  const ref = b.bookingRef || `BK-${b.id.slice(0, 6).toUpperCase()}`
                  const eventName = b.event?.eventName || '—'
                  const category = b.eventType?.label || b.providerCategory || '—'
                  const customer = b.user ? `${b.user.firstName} ${b.user.lastName}` : '—'
                  const email = b.user?.email || ''
                  const state = extractState(b.event?.venueAddress)
                  return (
                    <tr key={b.id}
                      style={{borderBottom: i < filtered.length - 1 ? '1px solid #f1f5f9' : 'none'}}
                      onMouseEnter={ev => (ev.currentTarget.style.background = '#fafafa')}
                      onMouseLeave={ev => (ev.currentTarget.style.background = 'transparent')}
                    >
                      <td style={{padding: '16px 20px', color: '#64748b', fontWeight: 500, whiteSpace: 'nowrap', fontSize: 13}}>{ref}</td>
                      <td style={{padding: '16px 20px', minWidth: 200}}>
                        <div style={{fontWeight: 700, color: '#0f172a'}}>{eventName}</div>
                        <div style={{fontSize: 12, color: '#94a3b8', marginTop: 2}}>{category}</div>
                      </td>
                      <td style={{padding: '16px 20px', minWidth: 180}}>
                        <div style={{fontWeight: 600, color: '#0f172a'}}>{customer}</div>
                        {email && <div style={{fontSize: 12, color: '#94a3b8', marginTop: 2}}>{email}</div>}
                      </td>
                      <td style={{padding: '16px 20px', color: '#374151', whiteSpace: 'nowrap'}}>{state}</td>
                      <td style={{padding: '16px 20px', color: '#374151', whiteSpace: 'nowrap'}}>{fmtDate(b.event?.eventDate)}</td>
                      <td style={{padding: '16px 20px', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap'}}>${Number(b.subtotal).toFixed(2)}</td>
                      <td style={{padding: '16px 20px', whiteSpace: 'nowrap'}}>
                        <span style={{padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: ss.bg, color: ss.color, border: `1px solid ${ss.border}`}}>
                          {displayStatus(b.status)}
                        </span>
                      </td>
                      <td style={{padding: '16px 20px', whiteSpace: 'nowrap'}}>
                        <button onClick={() => setViewing(b)}
                          style={{display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', fontWeight: 600, fontSize: 14, padding: 0}}>
                          <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                            <path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z'/><circle cx='12' cy='12' r='3'/>
                          </svg>
                          View
                        </button>
                      </td>
                    </tr>
                  )
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={8} style={{padding: '48px 20px', textAlign: 'center', color: '#94a3b8'}}>No bookings found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        <div style={{padding: '12px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <span style={{fontSize: 13, color: '#94a3b8'}}>Showing {filtered.length} of {bookingsTotal} bookings</span>
          <div style={{display: 'flex', gap: 8}}>
            {[
              {label: 'Confirmed', count: counts.confirmed, bg: '#f0fdf4', color: '#16a34a'},
              {label: 'Pending',   count: counts.pending,   bg: '#fefce8', color: '#ca8a04'},
              {label: 'Cancelled', count: counts.cancelled, bg: '#fff1f2', color: '#e11d48'},
            ].map(s => (
              <span key={s.label} style={{padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: s.bg, color: s.color}}>
                {s.label}: {s.count}
              </span>
            ))}
          </div>
        </div>
      </div>

      <Pagination page={bPage} limit={bLimit} total={bookingsTotal} onPageChange={setBPage} />

      {viewing && <BookingModal booking={viewing} onClose={() => setViewing(null)} />}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </>
      )}
    </div>
  )
}

const BookingsPageWrapper: FC = () => <BookingsPage />

export {BookingsPageWrapper}
